import { createAction } from 'typesafe-actions';

import { type ArtistPandoraId, type PandoraId } from 'src/types/shared';

import { BackstageActionTypes } from './constants';
import * as D from './deps';

interface GetCatalogDetailsForBackstagePayload {
  pandoraId: PandoraId;
  redirectOnNotFound: boolean;
  withCollaborations: boolean;
}

/**
 * @param {bool} redirectOnNotFound - if entity w pandoraId is not found, route to NotFound view
 */
export const getCatalogDetailsForBackstage = createAction(
  BackstageActionTypes.GET_CATALOG_DETAILS_FOR_BACKSTAGE,
  (pandoraId: PandoraId, redirectOnNotFound = false, withCollaborations = false) => ({
    pandoraId,
    redirectOnNotFound,
    withCollaborations,
  }),
)<GetCatalogDetailsForBackstagePayload>();
export type GetCatalogDetailsForBackstageAction = ReturnType<typeof getCatalogDetailsForBackstage>;

interface GetArtistEventsForBackstagePayload {
  artistPandoraId: ArtistPandoraId;
}

export const getArtistEventsForBackstage = createAction(
  BackstageActionTypes.GET_ARTIST_EVENTS_FOR_BACKSTAGE,
  (artistPandoraId: ArtistPandoraId) => ({ artistPandoraId }),
)<GetArtistEventsForBackstagePayload>();
export type GetArtistEventsForBackstageAction = ReturnType<typeof getArtistEventsForBackstage>;

interface GetArtistDiscographyForBackstagePayload {
  artistPandoraId: ArtistPandoraId;
  withCollaborations: $TSFixMe;
}

export const getArtistDiscographyForBackstage = createAction(
  BackstageActionTypes.GET_ARTIST_DISCOGRAPHY_FOR_BACKSTAGE,
  (artistPandoraId: ArtistPandoraId, withCollaborations = false) => ({
    artistPandoraId,
    withCollaborations,
  }),
)<GetArtistDiscographyForBackstagePayload>();
export type GetArtistDiscographyForBackstageAction = ReturnType<
  typeof getArtistDiscographyForBackstage
>;

type GetArtistDiscographyAnnotationsParams = $TSFixMe;

interface GetArtistDiscographyAnnotationsPayload {
  albumIds: GetArtistDiscographyAnnotationsParams;
}

export const getArtistDiscographyAnnotations = createAction(
  BackstageActionTypes.GET_ARTIST_DISCOGRAPHY_ANNOTATIONS,
  (albumIds: GetArtistDiscographyAnnotationsParams) => ({ albumIds }),
)<GetArtistDiscographyAnnotationsPayload>();
export type GetArtistDiscographyAnnotationsAction = ReturnType<
  typeof getArtistDiscographyAnnotations
>;

interface GetAllArtistTracksForBackstagePayload {
  artistPandoraId: ArtistPandoraId;
  withCollaborations: $TSFixMe;
}

export const getAllArtistTracksForBackstage = createAction(
  BackstageActionTypes.GET_ALL_ARTIST_TRACKS_FOR_BACKSTAGE,
  (artistPandoraId: ArtistPandoraId, withCollaborations = false) => ({
    artistPandoraId,
    withCollaborations,
  }),
)<GetAllArtistTracksForBackstagePayload>();
export type GetAllArtistTracksForBackstageAction = ReturnType<
  typeof getAllArtistTracksForBackstage
>;

interface AnnotateObjectsForBackstagePayload {
  pandoraIds: $TSFixMe[];
  useAnnotateObjects: boolean;
}

/**
 * Annotates backstage entities. Note that this would call the annotateObjectsSimple api by default.
 * If the useAnnotateObjects flag is passed, it would call annotateObjects instead. We should be
 * cautious of when we useAnnotateObjects because we may run the risk of fetching too many entities
 * at once and this would cause the garbage collection workers to be triggered.
 */
export const annotateObjectsForBackstage = createAction(
  BackstageActionTypes.ANNOTATE_OBJECTS_FOR_BACKSTAGE,
  (pandoraIds, useAnnotateObjects = false) => ({ pandoraIds, useAnnotateObjects }),
)<AnnotateObjectsForBackstagePayload>();
export type AnnotateObjectsForBackstageAction = ReturnType<typeof annotateObjectsForBackstage>;

type AnnotateObjectsForBackstageArtistAllSongsParams = $TSFixMe;

interface AnnotateObjectsForBackstageArtistAllSongsPayload {
  pandoraIds: AnnotateObjectsForBackstageArtistAllSongsParams;
}

export const annotateObjectsForBackstageArtistAllSongs = createAction(
  BackstageActionTypes.ANNOTATE_OBJECTS_FOR_BACKSTAGE_ARTIST_ALL_SONGS,
  (pandoraIds: AnnotateObjectsForBackstageArtistAllSongsParams) => ({ pandoraIds }),
)<AnnotateObjectsForBackstageArtistAllSongsPayload>();
export type AnnotateObjectsForBackstageArtistAllSongsAction = ReturnType<
  typeof annotateObjectsForBackstageArtistAllSongs
>;

type ShowedBackstagePageParams = $TSFixMe;

interface ShowedBackstagePagePayload {
  payload: ShowedBackstagePageParams;
}

export const showedBackstagePage = createAction(
  D.Constants.ViewActions.SHOWED_BACKSTAGE_PAGE,
  (payload: ShowedBackstagePageParams) => payload,
)<ShowedBackstagePagePayload>();
export type ShowedBackstagePageAction = ReturnType<typeof showedBackstagePage>;

type MutateBackstageDataParams = $TSFixMe;

interface MutateBackstageDataPayload {
  payload: MutateBackstageDataParams;
}

export const mutateBackstageData = createAction(
  BackstageActionTypes.MUTATE_BACKSTAGE_DATA,
  (payload: MutateBackstageDataParams) => payload,
)<MutateBackstageDataPayload>();
export type MutateBackstageDataAction = ReturnType<typeof mutateBackstageData>;
